// DEPENDENCIES ---------------------------------------------------------------- //

// import React from 'react';
import { useParams } from 'react-router-dom';
import { useRooftop } from '@store';

import { css } from '@emotion/css';
import { Theme, useMediaQuery, useTheme } from '@mui/material';

import InvoiceDetails from '@features/invoices/components/InvoiceDetails';
import InvoiceSidebar from '@features/invoices/components/InvoiceSidebar';

// COMPONENT ---------------------------------------------------------------- //

const InvoicesPage = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { id: paramInvoiceId } = useParams<{ id: string }>();
  const rooftop = useRooftop();

  // Below `md` there is not room for both, so they become one screen at a
  // time, the list until a row is chosen and then the invoice. The URL already
  // distinguishes them. A drawer was the other option, but an invoice is a
  // long document and reads badly in one.
  const stacked = useMediaQuery(theme.breakpoints.down('md'));
  const showDetail = !stacked || Boolean(paramInvoiceId);
  const showList = !stacked || !paramInvoiceId;

  return (
    <div className={cls.page}>
      {/* The list sits left of the invoice it opens, the way every other split
          screen in the portal reads. */}
      <div className={cls.flex}>
        {showList && (
          <div className={cls.flexSidebar}>
            <InvoiceSidebar rooftopId={rooftop?.id} selectedInvoiceId={paramInvoiceId ? parseInt(paramInvoiceId) : 0} />
          </div>
        )}

        {showDetail && (
          <div className={cls.flexDetails}>
            <InvoiceDetails
              rooftopId={rooftop?.id}
              selectedInvoiceId={paramInvoiceId ? parseInt(paramInvoiceId) : 0}
              backHref={stacked ? '/invoices' : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  page: css`
    position: relative;
  `,
  flex: css`
    display: flex;
    flex-wrap: nowrap;
    height: 100vh;
    padding-top: 64px;
    margin-top: -64px;
  `,
  flexDetails: css`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    flex: 1;
    /* Without this a wide table sets the flex item's floor and pushes the
       sidebar rather than scrolling within its own container. */
    min-width: 0;
    overflow-y: auto;
  `,
  flexSidebar: css`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    /* The charge columns beside it need about 860px to stay whole, so the list
       gives up what it can spare before those columns start dropping. */
    flex: 0 0 360px;
    ${theme?.breakpoints?.down('lg')} {
      flex-basis: 280px;
    }
    ${theme?.breakpoints?.down('md')} {
      flex: 1 1 100%;
      border-right: none;
    }
    overflow-y: auto;
    border-right: 1px solid ${theme?.palette?.divider};
    background-color: ${theme?.palette?.background?.paper};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default InvoicesPage;
